import React from "react";
import { Box, Button, Paper, TextField, useTheme } from "@mui/material";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { filterQueryFields, QueryField } from "system/query/QueryInfo";
import TitleCase from "components/formatting/TitleCase";
import { defaultQueryFieldTypeOrder } from "system/query/QueryUtility";

export interface QueryFieldListSelectorProps {
  fields: QueryField[];

  allowedFieldTypes?: string[];

  selectedFieldId?: string;

  setSelectedFieldId?: (fieldId: string) => void;

  // Only show fields that have the query_supported flag set to true
  requireQuerySupported?: boolean;

  // Only show fields that have the agg_supported flag set to true
  requireAggSupported?: boolean;

  disableClearable?: boolean;

  placeholder? : string | undefined;

  typeOrder?: string[];
}

export function QueryFieldListSelector(props: QueryFieldListSelectorProps): JSX.Element {
  const { i18n } = useTranslation();
  const theme = useTheme();

  const typeOrder = props.typeOrder || defaultQueryFieldTypeOrder;
  const [searchString, setSearchString] = useState<string>("");

  const uniqueTypeIDs = Array.from(new Set(props.fields.map(field => field._social_field_category || field.type)));

  const filteredFields = filterQueryFields( props.fields, {
    allowedFieldTypes: props.allowedFieldTypes,
    requireQuerySupported: props.requireQuerySupported,
    requireAggSupported: props.requireAggSupported,
    searchString: searchString
  });

  const groupedFields = uniqueTypeIDs.sort((a, b) => {
    // Sort by typeOrder if provided
    if (typeOrder.length > 0) {
      return typeOrder.indexOf(a) - typeOrder.indexOf(b);
    } else {
      return 0;
    }
  }).map(type => {
    // Find the fields that match the type
    const validFields = filteredFields?.filter(field => {
      if (field.type !== type && field._social_field_category !== type as string) {
        return false;
      }
      return true;
    }).sort((a, b) => {
      // Sort fields alphabetically by label
      return a.label.localeCompare(b.label);
    });

    return {
      type: type,
      fields: validFields
    };
  }).filter(group => {
    // Filter out groups with no fields
    return group.fields.length > 0;
  });

  const groupedFieldRows = groupedFields.map(group => {
    return (
      <Box key={group.type} sx={{ p:0, m:0 }}>
        <Box sx={{ pl:0.5, pt: 1, m:0, color: theme.palette.text.secondary }}>
          <TitleCase text={i18n.t([`query-social-field-categories.${group.type}`, group.type])} />
        </Box>
        <Box sx={{ p:0, m:0 }}>
          {group.fields.map(field => {
            return (
              <Button
                key={field.id}
                onClick={() => {
                  if (props.setSelectedFieldId) {
                    props.setSelectedFieldId(field.id);
                  }
                }}
                size="small"
                variant={props.selectedFieldId === field.id ? "contained" : "outlined"}
                sx={{ p:1, m:0.5, fontWeight: props.selectedFieldId === field.id ? "bold" : "normal" }}>
                <TitleCase text={field.label} />
              </Button>
            );
          })}
        </Box>
      </Box>
    );
  });

  return (
    <Box sx={{ p:0, m:0 }}>
      <Paper sx={{ p:1, pt: 1.5, m:0.5, mt: 0.5 }}>
      <TextField
        autoComplete="off"
        label={`${i18n.t('search')}...`}
        variant="outlined"
        fullWidth
        size="small"
        sx={{ m: 0, mb: 1 }}
        value={searchString || ""}
        onChange={((event: React.ChangeEvent<HTMLInputElement>) => {
          setSearchString(event.target.value);
        })}/>
      {props.fields && groupedFields.length === 0 &&
        <Box sx={{p:1, m: 1 }}>
          { i18n.t("query-form.no-valid-fields") }
        </Box>
      }
      </Paper>
      {groupedFields.length > 0 &&
        <Paper sx={{ p:1, m:0.5, mt: 2 }}>
          {groupedFieldRows}
        </Paper>
      }
    </Box>
  );
}
